import React, { useRef, useEffect } from 'react';
import { Terminal, Shield, Check, AlertTriangle, XCircle, Play } from 'lucide-react';
import { LiveEvent } from '../types';

interface LiveAuditFeedProps {
  events: LiveEvent[];
  onClear: () => void;
}

export const LiveAuditFeed: React.FC<LiveAuditFeedProps> = ({ events, onClear }) => {
  const listEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const renderEventBadge = (type: string) => {
    switch (type) {
      case 'TOOL_INVOKE_START':
        return <span className="log-tag tag-exec">INVOKE</span>;
      case 'TOOL_INVOKE_COMPLETE':
        return <span className="log-tag tag-done">SUCCESS</span>;
      case 'APPROVAL_REQUESTED':
        return <span className="log-tag tag-intercept">INTERCEPT</span>;
      case 'SAFEGUARD_BLOCKED':
        return <span className="log-tag tag-blocked">BLOCKED</span>;
      case 'APPROVAL_RESOLVED':
        return <span className="log-tag tag-done">RESOLVED</span>;
      default:
        return <span className="log-tag tag-exec">{type}</span>;
    }
  };

  const getRowClass = (type: string) => {
    if (type === 'TOOL_INVOKE_START') return 'exec';
    if (type === 'TOOL_INVOKE_COMPLETE') return 'done';
    if (type === 'SAFEGUARD_BLOCKED') return 'blocked';
    if (type === 'APPROVAL_REQUESTED') return 'intercept';
    return '';
  };

  return (
    <div className="log-stream-container">
      <div className="log-stream-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Terminal size={18} color="var(--accent-cyan)" />
          <span>Real-time Live Activity & Audit Stream</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({events.length} events logged)</span>
        </div>
        <button
          className="btn btn-secondary"
          style={{ padding: '4px 10px', fontSize: '0.78rem' }}
          onClick={onClear}
        >
          Clear Stream
        </button>
      </div>

      <div className="log-list">
        {events.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
            Listening for MCP tool execution and SafeGuard interception events...
          </div>
        ) : (
          events.map((evt, idx) => (
            <div key={evt.id || idx} className={`log-row ${getRowClass(evt.type)}`}>
              <span className="log-time">{new Date(evt.timestamp).toLocaleTimeString()}</span>
              {renderEventBadge(evt.type)}
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)', marginRight: '8px' }}>
                  {evt.payload?.toolName ? `[${evt.payload.toolName}]` : evt.type}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {evt.payload ? JSON.stringify(evt.payload) : ''}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={listEndRef} />
      </div>
    </div>
  );
};
